




































































































































































































































import {
  computed,
  defineComponent,
  reactive,
  ref,
  watch
} from '@vue/composition-api';
import { useQueryClient } from '@tanstack/vue-query';

import { debounce, merge, omit } from 'lodash';
import moment from 'moment';
import { IInvoice, InvoiceStateEnum, IQuote, Quote } from '@/models';
import {
  useFetchInvoices,
  useDeleteInvoice,
  useUpdateInvoiceState,
  useExportInvoicePdf
} from '@/composable/invoice';
import { IFetchInvoicesParams } from '@/services/invoice';
import authMixin from '@/mixins/auth';
import { updateFilters } from '@/utils/filters';

import ALPage from '@/components/core/Page/ALPage.vue';
import ErrorAlert from '@/components/common/ErrorAlert.vue';
import InvoiceDialog from '@/components/invoice/InvoiceDialog.vue';
import InvoiceStatusDisplay from '@/components/invoice/InvoiceStatusDisplay.vue';
import ConfirmationDialog from '@/components/common/ConfirmationDialog.vue';
import InvoiceOptionsButton from '@/components/invoice/InvoiceOptionsButton.vue';
import InvoiceSearchFilter from '@/components/invoice/InvoiceSearchFilter.vue';

export default defineComponent({
  name: 'InvoiceListPage',
  mixins: [authMixin],
  components: {
    ALPage,
    ConfirmationDialog,
    ErrorAlert,
    InvoiceDialog,
    InvoiceStatusDisplay,
    InvoiceOptionsButton,
    InvoiceSearchFilter
  },
  setup(_, { root }) {
    const filterParams = reactive<IFetchInvoicesParams>({
      search: root.$route.query.search as string,
      page: Number(root.$route.query.page ?? 1),
      quotes__id: Number(root.$route.query.quotes__id as string) || undefined,
      state: root.$route.query.state
        ? Number(root.$route.query.state)
        : undefined
    });

    const isSearchFilterActive = ref(false);
    const extraFiltersApplied = computed<boolean>(() =>
      Object.keys(omit(filterParams, ['page', 'search'])).some(
        key => filterParams[key as keyof IFetchInvoicesParams] != undefined
      )
    );

    //We use a separate ref for the search model in order to not update the queryKey once the search is updated
    const currentSearch = ref<string>(filterParams.search ?? '');

    const headers = [
      { text: 'Invoice #', value: 'number', sortable: false },
      { text: 'Customer Name', value: 'buyer_detail.name', sortable: false },
      { text: 'Proposal #', value: 'quote', sortable: false },
      { text: 'CSI Order #', value: 'csi_order_number', sortable: false },
      { text: 'PO #', value: 'po_number', sortable: false },
      { text: 'Payment Due Date', value: 'due_date', sortable: false },
      {
        text: 'Total Amount',
        value: 'total_amount_due',
        sortable: false
      },
      { text: 'Status', value: 'state', sortable: false, align: 'center' },
      { text: '', value: 'actions', sortable: false, width: 100 }
    ];

    const dialog = ref(false);
    const selectedInvoice = ref<IInvoice | null>(null);

    function openInvoiceDialog(invoice?: IInvoice) {
      dialog.value = true;
      selectedInvoice.value = invoice ?? null;
    }

    const {
      isLoading: deleteInProgress,
      error: deleteError,
      mutate: deleteInvoice
    } = useDeleteInvoice();

    const deleteDialog = ref(false);

    function openDeleteInvoiceDialog(invoice: IInvoice) {
      selectedInvoice.value = invoice;
      deleteDialog.value = true;
    }

    function closeDialog() {
      deleteDialog.value = false;
    }

    function removeInvoice(vehicle: IInvoice) {
      deleteInvoice(vehicle.id!, {
        onSuccess: () => {
          closeDialog();
        }
      });
    }

    const {
      isLoading: isSetStateLoading,
      error: setStateError,
      mutate: updateInvoiceState,
      reset: updateReset
    } = useUpdateInvoiceState();

    function setInvoiceState(invoice: IInvoice, state: InvoiceStateEnum) {
      clearAllErrors();
      updateInvoiceState({ id: invoice.id!, state });
    }

    function clearAllErrors() {
      updateReset();
      fetchError.value = null;
      setStateError.value = null;
    }

    const {
      invoices,
      error: fetchError,
      isLoading: isFetchLoading,
      count,
      queryKey
    } = useFetchInvoices(filterParams);

    const error = computed(
      () => fetchError.value || setStateError.value || exportPdfError.value
    );
    const isLoading = computed<boolean>(
      () =>
        isFetchLoading.value || isSetStateLoading.value || isGenPdfLoading.value
    );

    // CORE
    const queryClient = useQueryClient();

    const debounceFilterInvoices = debounce(function(
      filter: IFetchInvoicesParams
    ) {
      // mutates filters
      merge(filterParams, filter);
      queryClient.invalidateQueries({ queryKey: queryKey.value });
    },
    700);

    watch(
      () => filterParams,
      newParams => {
        root.$router
          .replace({
            name: root.$route.name!,
            query: newParams as Record<string, any>
          })
          .catch((e: Error) => e); //catch navigation duplication error
      },
      { immediate: true, deep: true }
    );

    function onUpdatePage(page: number) {
      filterParams.page = page;
    }

    function goToInvoicePage(invoice: IInvoice) {
      root.$router.push({
        name: `invoice-detail`,
        params: {
          id: String(invoice.id)
        }
      });
    }

    const {
      isLoading: isGenPdfLoading,
      error: exportPdfError,
      mutate: exportInvoicePdf,
      reset: updateExportReset
    } = useExportInvoicePdf();

    function exportPdf(invoice: IInvoice) {
      clearAllErrors();
      exportInvoicePdf(invoice, {
        onSuccess: () => {
          updateExportReset();
        }
      });
    }

    function getInvoiceProposalLabels(invoice: IInvoice): string[] {
      return invoice.quotes
        .map(quote =>
          typeof quote === 'number'
            ? String(quote)
            : (quote instanceof Quote ? quote : new Quote(quote)).toString()
        )
        .filter(label => label !== '');
    }

    return {
      InvoiceStateEnum,
      isSearchFilterActive,
      deleteInProgress,
      deleteDialog,
      deleteError,
      selectedInvoice,
      moment,
      dialog,
      filterParams,
      currentSearch,
      invoices,
      isLoading,
      headers,
      error,
      count,
      extraFiltersApplied,
      setInvoiceState,
      openInvoiceDialog,
      clearAllErrors,
      openDeleteInvoiceDialog,
      closeDialog,
      removeInvoice,
      goToInvoicePage,
      updateFilters,
      onUpdatePage,
      exportPdf,
      debounceFilterInvoices,
      getInvoiceProposalLabels
    };
  }
});
